import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, ShelfGrid } from '@cuito/ui';
import { shelvesApi, type LocationGridView } from '../api/shelves';
import CellEditorModal from '../components/shelf/CellEditorModal';
import ShelfMergeSelector from '../components/shelf/ShelfMergeSelector';
import { readServerMessage } from '../lib/apiError';
import { toUiShelf, type ShelfBox } from '../lib/shelfForm';


/**
 * 货架管理（`05-ui-design-system.md` §3.2 `/locations/:id/shelves`、`REQ-SHELF-012`）。
 *
 * 网格只画坐标底，货架是按 `rowStart/colStart/rowSpan/colSpan` 绝对定位的矩形区域
 * （`packages/ui` 的 `ShelfGrid`）。三种操作都在这里收口：合并（选择器）、拆分、编辑格子。
 * 服务端是权威：拆分/删除失败时把后端原文显示出来（例如 `SHELF_HAS_OPEN_ORDERS`）。
 */
export default function LocationShelvesPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [view, setView] = useState<LocationGridView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [selectedShelfId, setSelectedShelfId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (id === '') return;
    setLoading(true);
    setError(null);
    try {
      setView(await shelvesApi.getGrid(id));
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const boxes: ShelfBox[] = useMemo(
    () =>
      (view?.shelves ?? []).map((shelf) => ({
        shelfId: shelf.shelfId,
        code: shelf.code,
        rowStart: shelf.rowStart,
        colStart: shelf.colStart,
        rowSpan: shelf.rowSpan,
        colSpan: shelf.colSpan,
      })),
    [view],
  );

  const uiShelves = useMemo(() => (view?.shelves ?? []).map(toUiShelf), [view]);
  const selectedShelf = view?.shelves.find((shelf) => shelf.shelfId === selectedShelfId) ?? null;

  const split = async (): Promise<void> => {
    if (!selectedShelf) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await shelvesApi.splitShelf(selectedShelf.shelfId);
      setNotice(`已拆分为 ${result.createdShelves.length} 个 1×1 货架`);
      setSelectedShelfId(null);
      await load();
    } catch (e) {
      setError(readServerMessage(e) ?? '拆分失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  /**
   * 删除货架。
   *
   * **不用 `window.confirm`**：`05-ui-design-system.md:190` 要求所有不可逆操作走行内确认
   * 弹窗（`danger` 确认 / `secondary` 取消），并明确禁止浏览器原生 `confirm()`——
   * 原生弹窗无法被本仓库的组件测试断言，样式也不受设计系统控制。
   * 确认动作放在弹窗里（`confirmingDelete`），本函数只负责真正发请求。
   */
  const remove = async (): Promise<void> => {
    if (!selectedShelf) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await shelvesApi.deleteShelf(selectedShelf.shelfId);
      setConfirmingDelete(false);
      setSelectedShelfId(null);
      await load();
    } catch (e) {
      setError(readServerMessage(e) ?? '删除失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  if (id === '') {
    return <p className="font-bold text-red-600">请先选择地点</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          货架{view ? ` · ${view.location.name}` : ''}
        </h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/locations')}>
            ← 返回
          </Button>
          <Button
            onClick={() => setMerging((value) => !value)}
            disabled={busy || loading || view === null}
          >
            {merging ? '退出合并' : '合并相邻格子'}
          </Button>
        </div>
      </div>

      {error && <p className="font-bold text-red-600">{error}</p>}
      {notice && (
        <p className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">{notice}</p>
      )}

      {/*
        合并模式**只在拿到网格之后**才渲染选择器。
        取数未返回或失败时 `view === null`，这里若继续挂 `ShelfMergeSelector`，它会拿到
        一个凭空造出来的几何（旧实现是硬编码 3×6），用户照着假网格选区域、提交后收到
        400 `SHELF_OUT_OF_GRID`——错误发生在最不该发生的地方（用户什么都没做错）。
        按钮同时按 `loading || view === null` 禁用，两道门都关着。
      */}
      {merging && view !== null ? (
        <ShelfMergeSelector
          locationId={id}
          rows={view.grid.rows}
          cols={view.grid.cols}
          shelves={boxes}
          gridShelves={view.shelves}
          onCreated={() => {
            setNotice('货架已创建');
            setMerging(false);
            void load();
          }}
          onCancel={() => setMerging(false)}
        />
      ) : merging ? (
        <p className="py-8 text-center text-gray-500">
          {loading ? '加载中…' : '货架数据尚未就绪，无法进入合并模式'}
        </p>
      ) : (
        <>
          {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}

          {/* 网格几何只来自服务端：`view` 为空时连只读网格也不画（见上面的说明） */}
          {!loading && view !== null && (
            <div className="h-[60vh] w-full overflow-hidden rounded-lg border-2 border-black p-4">
              <ShelfGrid
                rows={view.grid.rows}
                cols={view.grid.cols}
                shelves={uiShelves}
                mode="manage"
                onSelectShelf={(shelfId) => setSelectedShelfId(shelfId)}
              />
            </div>
          )}

          {!loading && view !== null && view.shelves.length === 0 && (
            <p className="text-gray-500">该地点还没有上架商品，点击「合并相邻格子」开始划分货架</p>
          )}

          <section className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-black p-3">
            {selectedShelf === null ? (
              <span className="text-sm text-gray-500">点选一个货架后可拆分、删除或编辑格子</span>
            ) : (
              <>
                <span className="font-bold">
                  已选 {selectedShelf.code}
                  {selectedShelf.label ? `（${selectedShelf.label}）` : ''} · {selectedShelf.items.length} 个格子
                </span>
                <Button size="sm" onClick={() => setEditing(true)} disabled={busy}>
                  编辑格子
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void split()}
                  disabled={busy || (selectedShelf.rowSpan === 1 && selectedShelf.colSpan === 1)}
                >
                  拆分为 1×1
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={busy}
                >
                  删除货架
                </Button>
              </>
            )}
          </section>
        </>
      )}

      <CellEditorModal
        open={editing && selectedShelf !== null}
        shelf={selectedShelf}
        onClose={() => setEditing(false)}
        onChanged={() => void load()}
      />

      {/*
        行内确认弹窗（`05-ui-design-system.md:186-190` 的形态：danger 确认 + secondary 取消）。
        `selectedShelf !== null` 也写进 open 条件：重拉网格后选中的货架可能已经不在响应里，
        而「删除 null」没有意义。取消键不给 disabled，删除进行中也要能关掉它（请求不会被取消，
        但界面不该假死）。
      */}
      <Modal
        open={confirmingDelete && selectedShelf !== null}
        onClose={() => setConfirmingDelete(false)}
        title="删除货架"
      >
        <div className="flex flex-col gap-3">
          <p className="text-lg font-bold">确认删除货架？</p>
          <p className="text-sm text-gray-500">
            将删除货架「{selectedShelf?.code}」及其 {selectedShelf?.items.length ?? 0} 个格子。
            商品本身不受影响，此操作不可恢复。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
              取消
            </Button>
            <Button variant="danger" onClick={() => void remove()} disabled={busy}>
              {busy ? '删除中…' : '确认删除'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
